/**
 * A run's error string in `RunStrip`, under the run line — built for the errors the platform actually
 * returns: several lines, a destination's JSON body, unbroken tokens and URLs, sometimes kilobytes.
 *
 * CLAMPED TO TWO LINES, with a real `Show more` / `Show less` button (`aria-expanded`) that appears
 * only when the text overflows the clamp — measured, the way `ApprovalCard` measures its plan, and
 * re-measured on every reflow (the runs pane is resizable). Expanded, the text is still held to
 * `max-h-40` and scrolls inside itself, so even a huge error never pushes the stages or the run table
 * off screen. Expanded, it is a named `region`, and a tab stop only while it actually scrolls, so a
 * keyboard can scroll a long error without landing on one that fits.
 *
 * `whitespace-pre-wrap` keeps the platform's own line breaks and indentation readable;
 * `overflow-wrap:anywhere` breaks tokens that have no break opportunity (a URL, a base64 blob, a JSON
 * body with no spaces) instead of widening the strip. The text is never reformatted: what is on screen
 * is the platform's string.
 *
 * The whole string can be copied through the shared `CopyButton` (rule 9), with its inline check, since
 * the button is on screen at the moment of copy.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

interface RunErrorMessageProps {
  /** The platform's error string, verbatim — what is copied. */
  error: string;
  /** Says where it failed ("Extract failed: "); shown before the error, not copied. */
  prefix: string;
  className?: string;
}

export function RunErrorMessage({ error, prefix, className }: RunErrorMessageProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const [scrollable, setScrollable] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const textId = useId();

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;
    // Clamped, this decides the toggle. Expanded, the toggle must stay so the reader can clamp it
    // again, and the same measure decides whether the capped box scrolls.
    const measure = (): void => {
      const overflows = el.scrollHeight > el.clientHeight + 1;
      if (expanded) setScrollable(overflows);
      else {
        setOverflowing(overflows);
        setScrollable(false);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [expanded, error, prefix]);

  return (
    <div data-slot="run-error" className={cn('flex min-w-0 flex-col items-start gap-0.5', className)}>
      <div className="flex w-full min-w-0 items-start gap-1">
        <p
          ref={textRef}
          id={textId}
          data-expanded={expanded}
          // A paragraph cannot carry a name, so the expanded box is a region. It is a tab stop only
          // while it scrolls, so a keyboard reaches the rest of a long error.
          role={expanded ? 'region' : undefined}
          aria-label={expanded ? 'Run error' : undefined}
          tabIndex={expanded && scrollable ? 0 : undefined}
          className={cn(
            'min-w-0 flex-1 font-sans text-xs leading-[1.125rem] [overflow-wrap:anywhere] whitespace-pre-wrap text-danger',
            expanded ? 'max-h-40 overflow-y-auto rounded-sm' : 'line-clamp-2',
          )}
        >
          {prefix}
          {error}
        </p>
        <CopyButton
          text={error}
          copyLabel="run error"
          copiedFeedback
          variant="tertiary"
          iconOnly
          className="-mt-0.5 shrink-0 text-muted-foreground"
        />
      </div>
      {overflowing || expanded ? (
        <Button
          type="button"
          variant="text"
          className="text-xs text-primary hover:text-primary hover:underline"
          label={expanded ? 'Show less' : 'Show more'}
          aria-expanded={expanded}
          aria-controls={textId}
          onClick={() => {
            setExpanded((open) => !open);
          }}
        />
      ) : null}
    </div>
  );
}
